import React, { FC, useEffect, useState } from 'react';
import { format } from 'date-fns';
import CampaignDashboardTable from './CampaignDashboardTable/CampaignsDashboardTable';
import CampaignDashboardFilters from './CampaignDashboardFilters/CampaignDashboardFilters';

import { ICampaign, TCampaignDisplayType } from 'reduxState/store/campaigns/types';
import { AppGroup } from 'reduxState/store/application/types';
import { useGetCampaignsQuery } from 'reduxState/store/campaigns/api';
import { CheckboxOption } from 'components/Menu/MenuWithCheckboxes';

export interface ICampaignDashboardFilters {
  status: string[];
  startDate: Date | null;
  endDate: Date | null;
  sortBy: string;
  sortOrder: 'asc' | 'desc';
  searchTerm: string;
  displayType: TCampaignDisplayType[];
}

export const displayTypeOptions: CheckboxOption[] = [
  { label: 'Pop Up', value: 'POP_UP' },
  { label: 'Inline', value: 'INLINE' },
  { label: 'HTML Pop Up', value: 'HTML_POP_UP' },
  { label: 'HTML Pop Up Template', value: 'HTML_POP_UP_TEMPLATE' },
  { label: 'Alert HTML', value: 'ALERT_HTML' },
];

interface ICampaignDashboardProps {
  appGroup?: AppGroup;
  handleSetSelectedEditCampaign: (campaign: ICampaign | null) => void;
  handleSetSelectedDuplicatingCampaign: (campaign: ICampaign | null) => void;
}

const CampaignDashboard: FC<ICampaignDashboardProps> = ({
  appGroup,
  handleSetSelectedEditCampaign,
  handleSetSelectedDuplicatingCampaign,
}) => {
  const [filters, setFilters] = useState<ICampaignDashboardFilters>({
    status: ['ACTIVE', 'INACTIVE', 'EXPIRED', 'DISABLED'],
    startDate: null,
    endDate: null,
    searchTerm: '',
    displayType: ['POP_UP', 'INLINE', 'HTML_POP_UP', 'HTML_POP_UP_TEMPLATE', 'ALERT_HTML'],
    sortBy: 'modified_date', // defaulting for now
    sortOrder: 'asc', // defaulting for now
  });
  const { data: campaignsData, isFetching: isCampaignsFetching, isError: hasCampaignsError } = useGetCampaignsQuery({
    applicationIDs: appGroup?.applications.map(app => app.id) || [],
    merchantID: null,
    minStartDate: filters.startDate ? format(filters.startDate, "yyyy-MM-dd'T'HH:mm:ss'Z'") : '',
    maxEndDate: filters.endDate ? format(filters.endDate, "yyyy-MM-dd'T'HH:mm:ss'Z'") : '',
    sortBy: filters.sortBy,
    sortOrder: filters.sortOrder,
  });
  const [filteredCampaigns, setFilteredCampaigns] = useState<ICampaign[] | null>(null);

  useEffect(() => {
    let filteredCampaigns = campaignsData;

    if (!filteredCampaigns) return;

    // Filter by status - only include campaigns with selected statuses
    filteredCampaigns = filteredCampaigns.filter(campaign => filters.status.includes(campaign.status));

    // Filter by display type - exclude campaigns that don't match the selected display types
    filteredCampaigns = filteredCampaigns.filter(campaign => filters.displayType.includes(campaign.config.displayType));

    // Filter by search term - search in campaign title, removing special characters
    if (filters.searchTerm.trim()) {
      const normalizedSearchTerm = filters.searchTerm
        .replace(/[^a-zA-Z0-9\s]/g, '') // Remove special characters
        .toLowerCase()
        .trim();

      if (normalizedSearchTerm) {
        filteredCampaigns = filteredCampaigns.filter(campaign => {
          const normalizedTitle = campaign.title
            .replace(/[^a-zA-Z0-9\s]/g, '') // Remove special characters
            .toLowerCase();

          return normalizedTitle.includes(normalizedSearchTerm);
        });
      }
    }

    setFilteredCampaigns(filteredCampaigns || []);
  }, [campaignsData, filters, isCampaignsFetching]);

  return (
    <div className="flex flex-col w-full min-w-0">
      <CampaignDashboardFilters filters={filters} setFilters={setFilters} displayTypeOptions={displayTypeOptions} />
      <CampaignDashboardTable
        campaigns={filteredCampaigns || []}
        isFetching={isCampaignsFetching}
        hasError={hasCampaignsError}
        handleSetSelectedEditCampaign={handleSetSelectedEditCampaign}
        handleSetSelectedDuplicatingCampaign={handleSetSelectedDuplicatingCampaign}
      />
    </div>
  );
};

export default CampaignDashboard;
